import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Zap, Plus, Trash2, Clock } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getEvents, createEvent, deleteEvent } from '../api/buzz'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EventFlow } from '../components/EventFlow'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BuzzEvent } from '../types'

function fmtDuration(ms: number) {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${ms / 1000}s`
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`
  return `${Math.round(ms / 3_600_000)}h`
}

export function Events() {
  const fmt = useDateFmt()
  const qc = useQueryClient()

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => getEvents().then((r) => r.data),
  })

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ title: '', producer: '', description: '', lifeDurationMs: '' })
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => createEvent({
      title: form.title,
      producer: form.producer,
      description: form.description,
      lifeDurationMs: parseInt(form.lifeDurationMs),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['events'] }); setShowCreate(false); setForm({ title: '', producer: '', description: '', lifeDurationMs: '' }) },
  })

  const [search, setSearch] = useState('')
  const filtered = events.filter((ev: BuzzEvent) =>
    [ev.title, ev.producer, ev.description].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const [deleteTarget, setDeleteTarget] = useState<BuzzEvent | null>(null)
  const [flowEvent,    setFlowEvent]    = useState<BuzzEvent | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['events'] }); setDeleteTarget(null) },
  })

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Events</h1>
          <p className="text-sm text-ink-dim mt-0.5">Buzz event bus  publish and track events</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search events" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New event</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : events.length === 0 ? (
          <EmptyState icon={Zap} title="No events" description="Publish your first event to the bus" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Producer</th>
                <th>TTL</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev: BuzzEvent) => (
                <tr
                  key={ev.id}
                  onClick={() => setFlowEvent(ev)}
                  className="cursor-pointer"
                >
                  <td className="font-medium text-ink">{ev.title}</td>
                  <td className="text-ink-dim">{ev.producer}</td>
                  <td>
                    <span className="flex items-center gap-1 text-ink-dim">
                      <Clock size={12} /> {fmtDuration(ev.lifeDurationMs)}
                    </span>
                  </td>
                  <td>
                    <Badge variant={ev.isExpired ? 'default' : 'success'}>
                      {ev.isExpired ? 'Expired' : 'Active'}
                    </Badge>
                  </td>
                  <td className="text-ink-dim">{fmt(ev.createdAt)}</td>
                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev) }}
                      className="text-ink-dim hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New event" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            {[
              { key: 'title', label: 'Title *', placeholder: 'order.created' },
              { key: 'producer', label: 'Producer *', placeholder: 'order-service' },
              { key: 'description', label: 'Description', placeholder: 'Fired when a new order is created' },
              { key: 'lifeDurationMs', label: 'TTL (ms) *', placeholder: '86400000' },
            ].map(({ key, label, placeholder }) => (
              <Field key={key} label={label}>
                <input value={form[key as keyof typeof form]} onChange={set(key as keyof typeof form)} placeholder={placeholder}
                  type={key === 'lifeDurationMs' ? 'number' : 'text'}
                  className="field" />
              </Field>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!form.title || !form.producer || !form.lifeDurationMs} loading={create.isPending}>
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete event"
          message={`Delete event "${deleteTarget.title}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}

      {flowEvent && <EventFlow event={flowEvent} onClose={() => setFlowEvent(null)} />}
    </div>
  )
}
